import { ApplicationInsights, IExtendedConfiguration } from "@microsoft/1ds-analytics-web-js";
import { CorrelationVectorPlugin } from "@microsoft/1ds-cv-js";
import { IUCISModel } from "./Interfaces/interface";

// CommonSchema UTC flag that tells the Aria collector to scrub ext.ingest.clientIp.
const SCRUB_IP_FLAG = 0x100000; // 1048576 decimal

// Query parameters that carry authentication credentials, tokens, or PII.
// Compared case-insensitively (lower-cased) against every URL the SDK auto-captures.
const SENSITIVE_PARAMS: ReadonlySet<string> = new Set([
    "epct",               // EvoSTS Opaque Token (Entra ID)
    "epctrc",             // EvoSTS CANARY token (CredScan-flagged)
    "code",               // OAuth authorization code
    "token",              // generic token param
    "access_token",       // OAuth access token
    "id_token",           // OIDC ID token
    "refresh_token",      // OAuth refresh token
    "client_secret",      // OAuth client secret
    "jsh",                // EvoSTS — contains user email in plain text
    "jshs",               // EvoSTS session hint
    "login_hint",         // user email address
    "state",              // OAuth state (may contain tokens)
    "session_state",      // OIDC session state
    "sso_nonce",          // SSO nonce
    "client-request-id",  // correlation ID
]);

const sensitivePattern: string = Array.from(SENSITIVE_PARAMS)
    .map(p => p.replace(/[-/\\^$*+?.()|[\]{}]/g, "\\$&"))
    .join("|");

// Non-global — safe for .test() (no lastIndex side-effects)
const SENSITIVE_PARAM_TEST: RegExp = new RegExp(
    "[?&#](" + sensitivePattern + ")=",
    "i",
);

// Global — used only with .replace()
const SENSITIVE_PARAM_REPLACE: RegExp = new RegExp(
    "([?&#])(" + sensitivePattern + ")=([^&#]*)",
    "gi",
);

/**
 * Strips credential-bearing query and fragment parameters from a URL.
 * Returns the URL with sensitive values removed/redacted; falls back to regex
 * redaction (never an empty string) if the URL constructor throws.
 */
export function sanitizeUrl(url: string): string {
    if (!url) {
        return url;
    }

    try {
        const parsed = new URL(url, window.location.href);
        let modified = false;

        const keysToDelete: string[] = [];
        Array.from(parsed.searchParams.keys()).forEach((key: string) => {
            if (SENSITIVE_PARAMS.has(key.toLowerCase())) {
                keysToDelete.push(key);
            }
        });
        if (keysToDelete.length > 0) {
            keysToDelete.forEach(k => parsed.searchParams.delete(k));
            modified = true;
        }

        if (parsed.hash && parsed.hash.length > 1) {
            const origHash = parsed.hash;
            const scrubbedHash = origHash.replace(SENSITIVE_PARAM_REPLACE, "$1$2=[REDACTED]");
            if (scrubbedHash !== origHash) {
                parsed.hash = scrubbedHash;
                modified = true;
            }
        }

        return modified ? parsed.toString() : url;
    } catch {
        return url.replace(SENSITIVE_PARAM_REPLACE, "$1$2=[REDACTED]");
    }
}

const URL_FIELDS: ReadonlyArray<string> = ["refUri", "uri", "targetUri"];

/**
 * Sanitizes all URL-bearing fields on a telemetry item to prevent credential leaks.
 * Covers baseData.{refUri,uri,targetUri}, data.{refUri,uri,targetUri},
 * data.baseData.{refUri,uri,targetUri}, and a shallow scan of ext.web string fields
 * (ICM 783875880, 803347499, 31000000624148).
 */
function sanitizeTelemetryUrls(item: any): void {
    if (item.baseData) {
        for (const f of URL_FIELDS) {
            if (item.baseData[f]) {
                item.baseData[f] = sanitizeUrl(item.baseData[f]);
            }
        }
    }

    if (item.data) {
        for (const f of URL_FIELDS) {
            if (item.data[f]) {
                item.data[f] = sanitizeUrl(item.data[f]);
            }
        }

        if (item.data.baseData) {
            for (const f of URL_FIELDS) {
                if (item.data.baseData[f]) {
                    item.data.baseData[f] = sanitizeUrl(item.data.baseData[f]);
                }
            }
        }
    }

    if (item.ext && item.ext.web && typeof item.ext.web === "object") {
        const web = item.ext.web;
        for (const key of Object.keys(web)) {
            const val = web[key];
            if (typeof val === "string" && val.length > 10 && SENSITIVE_PARAM_TEST.test(val)) {
                web[key] = sanitizeUrl(val);
            }
        }
    }
}

// Pipeline plugin: stamps the ScrubIp flag on every event and sanitizes URLs to
// strip credential-bearing query params. Pipeline plugins are wired into the chain
// BEFORE any plugin's initialize() runs, so auto-captured events (pageView, onLoad)
// that fire during initialize() are also covered (ICM 759298390, ICM 783875880).
const scrubIpPlugin: any = {
    identifier: "ScrubIpPlugin",
    // tslint:disable-next-line:no-shadowed-variable
    initialize: (cfg: any, core: any, ext: any) => { /* no-op */ },
    isInitialized: () => true,
    priority: 190,
    processTelemetry: (item: any, itemCtx: any) => {
        item.ext = item.ext || {};
        item.ext.utc = item.ext.utc || {};
        // tslint:disable-next-line:no-bitwise
        item.ext.utc.eventFlags = (item.ext.utc.eventFlags || 0) | SCRUB_IP_FLAG;

        sanitizeTelemetryUrls(item);

        if (itemCtx) {
            itemCtx.processNext(item);
        }
    }
};


const analytics: ApplicationInsights = new ApplicationInsights();
let cvPlugin: CorrelationVectorPlugin = new CorrelationVectorPlugin();
const ucisData: IUCISModel = window.ucis;
let config: IExtendedConfiguration = {
    channelConfiguration: { // Post channel configuration
        eventsLimitInMem: 50
    },
    extensions: [
        cvPlugin,
        scrubIpPlugin
    ],
    instrumentationKey: ucisData?.OneDsToken,
    propertyConfiguration: { // Properties Plugin configuration
        // populateBrowserInfo: true,
        populateOperatingSystemInfo: true,
        userAgent: window.navigator.userAgent,
    },
    webAnalyticsConfiguration: { // Web Analytics Plugin configuration
        autoCapture: {
            click: true,
            jsError: true,
            onLoad: true,
            onUnload: true,
            pageView: true,
            resize: true,
            scroll: true
        }
    }
};

//Initialize SDK
analytics.initialize(config, []);


export default analytics;
